import React, { useEffect, useRef, useState, MouseEvent } from 'react';
import { Link as ReactRouterDOMLink, useLocation } from 'react-router-dom';
import { useForm, UseFormSetError } from 'react-hook-form';
import { css } from 'emotion';
import styled from '@emotion/styled';
import moment from 'moment';
import { LoadingIndicator } from '@spotify-internal/encore-web';
import queryString from 'query-string';

import { AnchorAPI } from 'modules/AnchorAPI';
import { ContactabilityStatus } from 'modules/AnchorAPI/contactability/fetchContactabilityLocationRule';
import {
  dayOptions,
  getMonthOptions,
  yearOptions,
} from 'modules/Date/constants';
import { debounce } from 'modules/debounce';
import { Button } from 'shared/Button/NewButton';
import { LinkText } from 'shared/Link';
import { Text } from 'shared/Text';
import Box from 'shared/Box';
import { ControlledCaptcha } from 'components/Captcha';
import { FieldCheckbox, FieldInput } from 'components/Field';
import { FieldSelect } from 'components/FieldSelect';
import { FormErrorAlert } from 'components/FormErrorAlert';
import { LocalizedDateSelector } from 'components/LocalizedDateSelector';
import events from 'components/SignupPageContainer/events';
import { fetchIsVanitySlugAvailable } from 'client/onboarding';
import { TFunction, useT } from 'client/i18n/hooks/useT';
import {
  BIRTHDATE_ERROR_MESSAGE,
  EXISTING_ACCOUNT_ERROR_MESSAGE,
  EXISTING_EMAIL_ERROR_MESSAGE,
  isValidEmail,
  isValidPassword,
  PASSWORD_ERROR_MESSAGE,
} from 'client/user';
import { paths } from 'client/shared/Paths';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { trackRiversideSwitchSignupClicked } from 'client/screens/IntegrationConnectionScreen/components/events';

const FieldsContainer = styled.div`
  width: 100%;
  max-width: 410px;
  margin: auto;
  overflow: hidden;
`;

const FieldWrapper = styled.div`
  margin-bottom: 10px;
`;

const DobWrapper = styled.div`
  margin: 15px 0 10px;

  > label {
    margin-bottom: 8px;
  }
`;

const DateSelect = styled(FieldSelect)`
  font-size: 1rem;
`;

const CatpchaWrapper = styled.div`
  margin-top: 24px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 32px auto 14px;
`;

const Terms = styled.div`
  margin-bottom: 21px;
  color: #7f8287;
  font-weight: normal;
  font-size: 11px;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
`;

type Data = {
  name: string;
  email: string;
  birthdate?: string;
  password: string;
  hasOptedIntoContactability: boolean;
  captcharesponse: string;
  vanitySlug?: string;
};

export type SignupFormProps = {
  feedUrl?: string;
  onSubmit: ({
    data,
    setError,
    locale,
  }: {
    data: Data;
    setError?: UseFormSetError<any>;
    locale: string;
  }) => Promise<void>;
  invalid: boolean;
  onClickRequestResetPassword: () => void;
  isLoading?: boolean;
  isV3Captcha?: boolean;
  loginUrl?: string;
  isImporting: boolean;
  isWordPressReferral: boolean;
  vanitySlug?: string;
  enableAutoFocus?: boolean;
  isIntegrationReferral?: boolean;
};

export const SignupForm = ({
  feedUrl = '',
  onSubmit,
  onClickRequestResetPassword,
  isLoading = false,
  isV3Captcha = false,
  loginUrl,
  isImporting,
  isWordPressReferral,
  vanitySlug,
  enableAutoFocus = true,
  isIntegrationReferral = false,
}: SignupFormProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, dirtyFields },
    register,
    control,
    getValues,
    setValue,
    trigger,
    setError,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      vanitySlug,
      name: '',
      email: '',
      password: '',
      dobMonth: '',
      dobDay: '',
      dobYear: '',
      captcharesponse: '',
      hasOptedIntoContactability: false,
    },
    shouldFocusError: true,
  });
  const t = useT();
  const { locale } = useLocale();
  const { search } = useLocation();

  const [showContactCheckbox, setShowContactCheckbox] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const { current: debouncedOnVanityChange } = useRef(
    debounce(async () => {
      await trigger('vanitySlug');
    }, 500)
  );
  const switchParams = queryString.parse(decodeURIComponent(search));

  // If we receive new vanity slug prop, use it. This is for when ImportCreateAccount
  // fetches vanitySlug for the first time and asynchronously passes the prop here.
  useEffect(() => {
    setValue('vanitySlug', vanitySlug);
  }, [setValue, vanitySlug]);

  const [isVanityLoading, setVanityLoading] = useState(false);

  /**
   * Sets the `hasOptedIntoContactability` checkbox value
   * Hides/renders the checkbox based on user location contactability rules
   */
  useEffect(() => {
    AnchorAPI.fetchContactabilityLocationRule()
      .then(({ countryRule }) => {
        const defaultCheckboxValue =
          countryRule === ContactabilityStatus.OPTED_IN ||
          countryRule === ContactabilityStatus.OPTED_IN_SECONDARY_RESPONSE ||
          countryRule === ContactabilityStatus.NEEDS_RESPONSE_OPT_IN;
        setValue('hasOptedIntoContactability', defaultCheckboxValue);
        if (countryRule && countryRule !== ContactabilityStatus.OPTED_IN) {
          setShowContactCheckbox(true);
        }
      })
      .catch(err => {
        throw new Error(err.message);
      });
  }, [setValue]);

  const trackClickSignUp = () => {
    events.signUpRegisterButtonClicked();
  };

  const onSignupClicked = () => {
    if (switchParams?.partner === 'riverside') {
      trackRiversideSwitchSignupClicked();
    } else {
      trackClickSignUp();
    }
  };

  const dobError = errors.dobDay || errors.dobMonth || errors.dobYear;
  const isSubmitDisabled = isLoading || isSubmitting;

  const finalLoginUrl =
    loginUrl ||
    `/login${feedUrl ? `?feedUrl=${encodeURIComponent(feedUrl)}` : ''}`;

  const vanityRegistration = vanitySlug
    ? register('vanitySlug', {
        validate: {
          checkUnique: async data => {
            setVanityLoading(true);
            const result = await fetchIsVanitySlugAvailable(data);
            setVanityLoading(false);
            return result;
          },
        },
      })
    : undefined;

  return (
    <form
      onSubmit={
        !isSubmitDisabled
          ? handleSubmit(({ dobDay, dobMonth, dobYear, ...rest }) => {
              const data = {
                birthdate: `${dobYear}-${dobMonth}-${dobDay}`,
                ...rest,
              };
              // Clear any exisiting error message
              setErrorMessage(null);
              // Make sure to resolve promise so isSubmitting is accurate
              return onSubmit({ data, setError, locale }).catch(({ message }) =>
                setErrorMessage(message)
              );
            })
          : () => {}
      }
      id="SignupForm"
      method="POST"
      noValidate={true}
    >
      <FieldsContainer>
        {vanityRegistration && (
          <>
            <div
              className={css`
                display: flex;
                align-items: center;
              `}
            >
              <p>
                <strong>anchor.fm/</strong>
              </p>
              <FieldInput
                {...vanityRegistration}
                name="vanitySlug"
                type="text"
                onChange={e => {
                  vanityRegistration.onChange(e);
                  setVanityLoading(true);
                  // Trigger the async validation
                  debouncedOnVanityChange();
                }}
                containerClassName={css`
                  flex-grow: 1;
                `}
              />
            </div>
            <div
              className={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                margin: 4px 0 6px 0;
              `}
            >
              {isVanityLoading ? (
                <LoadingIndicator indicatorSize={LoadingIndicator.sm} />
              ) : !dirtyFields.vanitySlug ? (
                <p
                  className={css`
                    width: 100%;
                    color: #5f6369;
                    text-align: right;
                    opacity: 0.5;
                  `}
                >
                  {t(
                    'SignupForm.CustomURLTipText',
                    'Tip: you can customize this URL.',
                    'Anchor custom URL field tip text'
                  )}
                </p>
              ) : !errors.vanitySlug ? (
                <p
                  className={css`
                    color: #27de56;
                  `}
                >
                  {t(
                    'SignupForm.CustomURLFieldValueAcceptedMessage',
                    'Looks good!',
                    'Anchor custom URL field value valid message'
                  )}
                </p>
              ) : (
                <p
                  className={css`
                    color: #ff6663;
                  `}
                >
                  {t(
                    'SignupForm.CustomURLFieldValueRejectedMessage',
                    'Sorry, that URL is unavailable.',
                    'Anchor custom URL field value already in use message'
                  )}
                </p>
              )}
            </div>
          </>
        )}
        <FieldWrapper>
          <FieldInput
            {...register('name', {
              required: t(
                'SignupForm.NameFieldRequiredMessage',
                'Name is required',
                'Name field required message'
              ),
            })}
            error={errors.name}
            placeholder={t(
              'SignupForm.NameFieldText',
              'Full Name',
              'Name field text'
            )}
            type="text"
            autoComplete="name"
            aria-label={t(
              'SignupForm.NameFieldText',
              'Full Name',
              'Name field text'
            )}
            autoFocus={enableAutoFocus}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldInput
            {...register('email', {
              required: t(
                'SignupForm.EmailFieldRequiredMessage',
                'Email is required',
                'Email field required message'
              ),
              validate: email =>
                isValidEmail(email) ||
                t(
                  'SignupForm.EmailFieldInvalidMessage',
                  'Email is invalid',
                  'Email field invalid message'
                ),
            })}
            error={errors.email}
            placeholder={t(
              'SignupForm.EmailFieldText',
              'Email',
              'Email field text'
            )}
            type="email"
            autoComplete="email"
            aria-label={t(
              'SignupForm.EmailFieldText',
              'Email',
              'Email field text'
            )}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldInput
            {...register('password', {
              required: t(
                'SignupForm.PasswordFieldRequiredMessage',
                'Password is required',
                'Password field required message'
              ),
              validate: {
                minLength: pw =>
                  pw.length >= 7 ||
                  t(
                    'SignupForm.PasswordFieldInvalidMessage',
                    'Passwords must be 7 characters or more',
                    'Password field invalid message'
                  ),
                valid: pw => isValidPassword(pw) || PASSWORD_ERROR_MESSAGE,
              },
            })}
            error={errors.password}
            placeholder={t(
              'SignupForm.PasswordFieldText',
              'Password',
              'Password field text'
            )}
            type="password"
            autoComplete="new-password"
            aria-label={t(
              'SignupForm.PasswordFieldText',
              'Password',
              'Password field text'
            )}
          />
        </FieldWrapper>
        <DobWrapper>
          {/** Ignoring eslint because it is not picking up the htmlFor and control id */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="dobMonth">
            {t(
              'SignupForm.BirthdayFieldLabel',
              "What's your date of birth?",
              'Date of birth field label'
            )}
          </label>
          <LocalizedDateSelector
            daySelector={
              <DateSelect
                {...register('dobDay', {
                  required: true,
                  validate: day => {
                    const month = getValues('dobMonth');
                    const year = getValues('dobYear');
                    if (!month || !year) return true;
                    // Ensure date is valid (e.g. not Feb 31)
                    return (
                      moment(`${year}-${month}-${day}`).isValid() ||
                      t(
                        'SignupForm.DateInvalidMessage',
                        'Date is not possible',
                        'Date invalid message'
                      )
                    );
                  },
                })}
                id="dobDay"
                options={dayOptions}
                isShowingError={!!errors.dobDay}
                placeholder={t(
                  'SignupForm.DayFieldPlaceholder',
                  'Day',
                  'Day field placeholder'
                )}
                aria-label={t(
                  'SignupForm.DayFieldAriaLabel',
                  'Birthday date',
                  'Day field screenreader information'
                )}
                required
              />
            }
            monthSelector={
              <DateSelect
                {...register('dobMonth', {
                  required: true,
                })}
                id="dobMonth"
                options={getMonthOptions(t)}
                isShowingError={!!errors.dobMonth}
                placeholder={t(
                  'SignupForm.MonthFieldPlaceholder',
                  'Month',
                  'Month field placeholder'
                )}
                aria-label={t(
                  'SignupForm.MonthFieldAriaLabel',
                  'Birthday month',
                  'Month field screenreader information'
                )}
                required
              />
            }
            yearSelector={
              <DateSelect
                {...register('dobYear', {
                  required: true,
                })}
                id="dobYear"
                options={yearOptions}
                isShowingError={!!errors.dobYear}
                placeholder={t(
                  'SignupForm.YearFieldPlaceholder',
                  'Year',
                  'Year field placeholder'
                )}
                aria-label={t(
                  'SignupForm.YearFieldAriaLabel',
                  'Birthday year',
                  'Year field screenreader information'
                )}
                required
              />
            }
          />
          {dobError && (
            <div
              className={css`
                margin-top: 4px;
                color: #d0021b;
                font-size: 0.875rem;
                line-height: 1.125rem;
              `}
              id="dob-error"
            >
              {errors.dobDay?.message ||
                t(
                  'SignupForm.DateRequiredMessage',
                  'Date of birth is required',
                  'Date required message'
                )}
            </div>
          )}
        </DobWrapper>
        <CatpchaWrapper>
          <ControlledCaptcha
            control={control}
            name="captcharesponse"
            isV3={isV3Captcha}
            rules={{
              required: t(
                'SignupForm.ReCAPTCHARequiredMessage',
                'Please complete the reCAPTCHA',
                'reCAPTCHA required message'
              ),
            }}
          />
        </CatpchaWrapper>
      </FieldsContainer>
      {errorMessage && (
        <Box display="flex" justifyContent="center">
          <Box maxWidth={404} width="100%">
            {(() => {
              switch (errorMessage) {
                case EXISTING_ACCOUNT_ERROR_MESSAGE:
                  return (
                    <FormErrorAlert
                      title={t(
                        'SignupForm.ExistingAccountErrorTitle',
                        'Existing account found',
                        'Sign up form existing account error title'
                      )}
                      renderMessage={
                        <>
                          {t(
                            'SignupForm.ExistingAccountErrorMessage',
                            'We found an existing account that uses these credentials. You can {loginLink} or try again with a different email address.',
                            'Sign up form existing account error message',
                            {
                              loginLink: (
                                <ReactRouterDOMLink
                                  key="sign-up-form__login-link"
                                  to={finalLoginUrl}
                                  onClick={() =>
                                    events.signUpSignInButtonClicked()
                                  }
                                >
                                  {t(
                                    'SignupForm.LoginLinkText',
                                    'log in',
                                    'log in link text'
                                  )}
                                </ReactRouterDOMLink>
                              ),
                            }
                          )}
                        </>
                      }
                    />
                  );
                case EXISTING_EMAIL_ERROR_MESSAGE:
                  return (
                    <FormErrorAlert
                      title={t(
                        'SignupForm.ExistingEmailErrorTitle',
                        'This email address is already being used',
                        'Sign up form email already in use error title'
                      )}
                      renderMessage={
                        <>
                          {t(
                            'SignupForm.ExistingEmailErrorMessage',
                            "We found an existing account with this email address, but this password doesn't match. You can try again or {resetPasswordLink}.",
                            'Sign up form email already in use error message',
                            {
                              resetPasswordLink: (
                                <ReactRouterDOMLink
                                  key="sign-up-form__reset-password-link"
                                  to={finalLoginUrl}
                                  onClick={onClickRequestResetPassword}
                                >
                                  {t(
                                    'SignupForm.ResetPasswordLinkText',
                                    'reset your password',
                                    'Reset password link text'
                                  )}
                                </ReactRouterDOMLink>
                              ),
                            }
                          )}
                        </>
                      }
                    />
                  );
                case BIRTHDATE_ERROR_MESSAGE:
                  return (
                    <FormErrorAlert
                      title={t(
                        'SignupForm.BirthDateErrorMessage',
                        "Sorry, you don't meet Anchor's age requirements.",
                        'Signup form birth date error message'
                      )}
                    />
                  );
                default:
                  return (
                    <FormErrorAlert
                      title={t(
                        'SignupForm.GenericErrorTitle',
                        'Unable to create account',
                        'Sign up form generic error title'
                      )}
                      renderMessage={
                        <>
                          {t(
                            'SignupForm.GenericErrorMessage',
                            'Sorry, but we were unable to create your account. Please refresh the page and try again, or visit {helpPageLink} so we can help you troubleshoot.',
                            'Sign up form generic error message',
                            {
                              helpPageLink: (
                                <LinkText
                                  key="sign-up-form__help-page-link"
                                  to={paths.getHelpPage('home')}
                                  isInline={true}
                                >
                                  {paths.getHelpPage('home')}
                                </LinkText>
                              ),
                            }
                          )}
                        </>
                      }
                    />
                  );
              }
            })()}
          </Box>
        </Box>
      )}
      <ButtonWrapper>
        <Button
          className={css`
            width: 100%;
          `}
          onClick={onSignupClicked}
          type="submit"
          color="purple"
          isDisabled={isSubmitDisabled}
        >
          {t('Button.Signup', 'Sign up', 'Sign up button text')}
        </Button>
      </ButtonWrapper>
      <Terms>
        {showContactCheckbox && (
          <FieldCheckbox
            {...register('hasOptedIntoContactability')}
            type="checkbox"
            label={t(
              'SignupForm.SendAnchorNewsCheckboxLabel',
              'Please send me news and offers from Anchor',
              'Anchor news and offer opt-in checkbox label'
            )}
            containerClassName={css`
              margin-top: 17px;
              margin-bottom: 14px;
            `}
          />
        )}
        {!isWordPressReferral && !isIntegrationReferral && (
          <LoginImportLink
            isImporting={isImporting}
            finalLoginUrl={finalLoginUrl}
            t={t}
          />
        )}
        {!isIntegrationReferral && (
          <div
            className={css`
              margin-top: 32px;
              color: #292f36;
            `}
          >
            {t(
              'SignupForm.TermsOfServiceAgreementMessage',
              'By continuing, you agree to our {termsOfServiceLink} and {privacyPolicyLink}.',
              'Sign up terms of service agreement message',
              {
                termsOfServiceLink: (
                  <LinkText
                    key="sign-up-form__tos-link"
                    target="_blank"
                    onClick={() =>
                      events.signUpLegalLinkClicked({ link: 'tos' })
                    }
                    isInline={true}
                    to={paths.legal.tos}
                    color="gray"
                  >
                    {t(
                      'Link.TermsOfService',
                      'Terms of Service',
                      'Terms of Service link text'
                    )}
                  </LinkText>
                ),
                privacyPolicyLink: (
                  <LinkText
                    key="sign-up-form__privacy-policy-link"
                    target="_blank"
                    onClick={() =>
                      events.signUpLegalLinkClicked({ link: 'privacy' })
                    }
                    isInline={true}
                    color="gray"
                    to={paths.legal.privacy}
                  >
                    {t(
                      'Link.PrivacyPolicy',
                      'Privacy Policy',
                      'Privacy Policy link text'
                    )}
                  </LinkText>
                ),
              }
            )}
          </div>
        )}
      </Terms>
    </form>
  );
};

function LoginImportLink({
  isImporting,
  finalLoginUrl,
  t,
}: Pick<SignupFormProps, 'isImporting'> & { finalLoginUrl: string } & {
  t: TFunction;
}) {
  return isImporting ? (
    <div
      className={css`
        margin-bottom: 32px;
      `}
    >
      <Text size="md" align="center">
        {t(
          'SignupForm.LoginInsteadMessage',
          'Already have an account? {loginLink} instead.',
          'Sign up form login instead message',
          {
            loginLink: (
              <Text
                size="md"
                isBold={true}
                isInline={true}
                key="sign-up-form__login-link"
              >
                <ReactRouterDOMLink to={finalLoginUrl}>
                  {t('SignupPage.LoginLinkText', 'Log in', 'Log in link text')}
                </ReactRouterDOMLink>
              </Text>
            ),
          }
        )}
      </Text>
    </div>
  ) : (
    <p
      className={css`
        font-size: 0.875rem;
        text-align: center;
      `}
    >
      {t(
        'SignupForm.ImportPodcastMessage',
        'You can also {importLink} an existing podcast.',
        'Sign up form import podcast message',
        {
          importLink: (
            <ReactRouterDOMLink
              key="sign-up-form__import-link"
              data-cy="importLink"
              to={paths.anchor.switchForm}
              onClick={events.signUpImportButtonClicked}
            >
              {t(
                'SignupForm.ImportPodcastLinkText',
                'import',
                'Import podcast link text'
              )}
            </ReactRouterDOMLink>
          ),
        }
      )}
    </p>
  );
}
